import { defineStore } from "pinia";

import { IDevice } from "../../models/Devices/device";
import { IDurationDeviceRequest } from "../../models/Devices/durationDeviceRequest";
import DevicePropertyForm from "../../models/forms/devicePropertyForm";

import { DeviceTypes, PumpDeviceProperties } from "../../constantes/constants";
import { useDeviceStore } from "../device.store";
import { useUsersStore } from "../users.store";
import { useParameterStore } from "../parameter.store";
import { useDevicePropertyStore } from "../deviceProperty.store";

export const usePumpStore = defineStore('pump', {
    state: () => {
        return {
            selectedPumpId: 0 as number,
            selectedPumpProperties: [] as DevicePropertyForm[],

            availablePumps: [] as IDevice[],

            volumeToDeliver: 0 as number
        }
    },
    getters: {
        hasActivePump: (state):boolean => {
            return state.selectedPumpId != 0;
        },

        selectedPump: (state):IDevice|undefined => {
            return state.availablePumps.find(pump => pump.id == state.selectedPumpId);
        },

        rotationSpeed: (state):DevicePropertyForm|undefined => {
            return state.selectedPumpProperties.find(p => p.key == PumpDeviceProperties.ROTATION_SPEED);
        },

        direction: (state):DevicePropertyForm|undefined => {
            return state.selectedPumpProperties.find(p => p.key == PumpDeviceProperties.DIRECTION);
        },

        twoSecondsCalibrationProperty: (state): DevicePropertyForm | undefined => {
            return state.selectedPumpProperties.find(p => p.key == PumpDeviceProperties.CALIBRATION_2_SEC);
        },

        fifteenSecondsCalibrationProperty: (state): DevicePropertyForm | undefined => {
            return state.selectedPumpProperties.find(p => p.key == PumpDeviceProperties.CALIBRATION_15_SEC);
        },

        twoSecondsCalibration: (state):number => {
            let props = state.selectedPumpProperties.find(p => p.key == PumpDeviceProperties.CALIBRATION_2_SEC);
            if ( props == undefined)
            {
                props = new DevicePropertyForm();
                props.key = PumpDeviceProperties.CALIBRATION_2_SEC;
                props.value = "0";
            }
            return Number(props.value) || 0;
        },

        fifteenSecondsCalibration: (state):number => {
            let props = state.selectedPumpProperties.find(p => p.key == PumpDeviceProperties.CALIBRATION_15_SEC);
            if ( props == undefined)
            {
                props = new DevicePropertyForm();
                props.key = PumpDeviceProperties.CALIBRATION_15_SEC;
                props.value = "0";
            }
            return Number(props.value) || 0;
        },

        averageDispatchPerSecond : (state) => (isInteger: boolean = false): number => {
            let twos = 0;
            let fifteen = 0;
            let props2s = state.selectedPumpProperties.find(p => p.key == PumpDeviceProperties.CALIBRATION_2_SEC);
            let props15s = state.selectedPumpProperties.find(p => p.key == PumpDeviceProperties.CALIBRATION_15_SEC);
            if ( props2s != undefined)
                twos = props2s.value != undefined ? Number(props2s.value) : 0;
            if ( props15s != undefined)
                fifteen = props15s.value != undefined ? Number(props15s.value) : 0;

            if(twos == 0 || fifteen == 0)
                return 0

            let avg = (fifteen - twos) / 13;
            avg = Math.ceil(avg * 100) / 100;

            return isInteger ? Math.floor(avg) : avg;
        },

        deliveryETA :(state):number => {
            if(state.volumeToDeliver <= 0)
                return 0;
            let fifteen = 0;
            let twos = 0;
            let avg = 0;
            let props2s = state.selectedPumpProperties.find(p => p.key == PumpDeviceProperties.CALIBRATION_2_SEC);
            let props15s = state.selectedPumpProperties.find(p => p.key == PumpDeviceProperties.CALIBRATION_15_SEC);
            if ( props2s != undefined)
                twos = props2s.value != undefined ? Number(props2s.value) : 0;
            if ( props15s != undefined)
                fifteen = props15s.value != undefined ? Number(props15s.value) : 0;

            if(twos == 0 || fifteen == 0)
                avg = 0;
            else
                avg = (fifteen - twos) / 13;
            avg = Math.ceil(avg * 100) / 100;

            if(state.volumeToDeliver < twos)
                state.volumeToDeliver = twos

            let remainingDispatch = state.volumeToDeliver - twos;
            let remainingDispatchTime = twos == 0 ? 0 : remainingDispatch / avg;
            
            return Math.ceil((2 + remainingDispatchTime) * 100) / 100;
        },

        isVolumeToDeliverValid : (state):boolean => {
            let twos = 0;
            let props = state.selectedPumpProperties.find(p => p.key == PumpDeviceProperties.CALIBRATION_2_SEC);
            if (props == undefined)
                return true;
            else {
                twos = props.value != undefined ? Number(props.value) : 0;
                return state.volumeToDeliver >= twos;
            }
                
        }
    },
    actions: {
        init() {
            this.availablePumps = [];

            const deviceStore = useDeviceStore();
            const paramStore = useParameterStore();
            
            let pumpTypeId = paramStore.getParameterByCode3('DEVICE', 'TYPE', DeviceTypes.POMPE_DILUTION)?.id;
            this.availablePumps = deviceStore.getDeviceByTypeForCurrentCenter(pumpTypeId!);
            
            if(this.availablePumps.length > 0) {
                this.selectedPumpId = this.availablePumps[0].id;
                this.refreshProperties();
            }
        },

        async refreshProperties() {
            const devicePropertyStore = useDevicePropertyStore();
            devicePropertyStore.currentDeviceId = this.selectedPumpId;
            this.selectedPumpProperties = devicePropertyStore.getCurrentDeviceProperties.map(p => devicePropertyStore.toForm(p));
        },

        setTwoSecondsCalibrationProperty(value: string | number) {
            const prop = this.selectedPumpProperties.find(p => p.key == PumpDeviceProperties.CALIBRATION_2_SEC);
            if (prop) {
                prop.value = String(value);
            }
        },

        setFifteenSecondsCalibrationProperty(value: string | number) {
            const prop = this.selectedPumpProperties.find(p => p.key == PumpDeviceProperties.CALIBRATION_15_SEC);
            if (prop) {
                prop.value = String(value);
            }
        },

        async onPumpSelected() {
            if(this.hasActivePump) {
                await this.refreshProperties();
            }
            else {
                console.error("No pump device for which to load properties found");
            }
        },

        async startPump(connectionTest: boolean = false) {
            if(this.hasActivePump) {
                const deviceStore = useDeviceStore();
    
                let durationDeviceRequest: IDurationDeviceRequest = {
                    duration: connectionTest ? 0 : this.deliveryETA
                };
    
                await deviceStore.sendDurationMeasurementInstructions(this.selectedPumpId, durationDeviceRequest);
            }
            else {
                console.error("No pump device found to process the request");
            }
        },

        async startPumpForDuration(duration: number) {
            if(this.hasActivePump) {
                const deviceStore = useDeviceStore();
    
                let durationDeviceRequest: IDurationDeviceRequest = {
                    duration: duration
                };
    
                await deviceStore.sendDurationMeasurementInstructions(this.selectedPumpId, durationDeviceRequest);
            }
            else {
                console.error("No pump device found to process the request");
            }
        },

        async stopPump() {
            if(this.hasActivePump) {
                const deviceStore = useDeviceStore();    
                await deviceStore.cancelMeasurementRequest(this.selectedPumpId);
            }
            else {
                console.error("No pump device found to process the request");
            }
        }
    }
})